import React, { useRef, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	CircularProgress,
	Alert,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import styles from '../SchedulerSetting.module.scss';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

export interface EventDocumentPdfEditorModalProps {
	open: boolean;
	onClose: () => void;
	/** Blob URL or remote PDF URL — parent manages blob lifecycle for local files */
	pdfUrl: string | null;
	documentTitle: string;
	onSave: (file: File) => void | Promise<void>;
}

/**
 * Onscreen PDF editing using WebViewer (same stack as onboarding document review).
 * Exports filled forms + annotations into a single PDF File on save.
 */
export function EventDocumentPdfEditorModal({
	open,
	onClose,
	pdfUrl,
	documentTitle,
	onSave,
}: EventDocumentPdfEditorModalProps): JSX.Element {
	const viewer = useRef<HTMLDivElement>(null);
	const instance = useRef<any>(null);
	const [isViewerReady, setIsViewerReady] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { showToast } = useToastContext();

	useEffect(() => {
		if (!open || !pdfUrl) {
			return;
		}

		const dispose = () => {
			if (instance.current) {
				try {
					instance.current.UI.dispose();
				} catch {
					/* ignore */
				}
				instance.current = null;
			}
			setIsViewerReady(false);
		};

		const t = setTimeout(() => {
			dispose();
			setError(null);

			if (!viewer.current) {
				return;
			}

			WebViewer(
				{
					path: `/webviewer`,
					licenseKey:
						'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5',
					initialDoc: pdfUrl,
					fullAPI: true,
					disableStreaming: true,
				} as any,
				viewer.current
			)
				.then((inst) => {
					instance.current = inst;
					setIsViewerReady(true);
					inst.Core.documentViewer.addEventListener('documentLoaded', () => {
						inst.UI.setZoomLevel(1);
					});
					inst.Core.documentViewer.addEventListener('documentLoadError', () => {
						setError('Failed to load PDF. Check the file or URL and try again.');
					});
				})
				.catch((err) => {
					console.error('WebViewer initialization error:', err);
					setError('Failed to initialize PDF viewer');
					setIsViewerReady(false);
				});
		}, 100);

		return () => {
			clearTimeout(t);
			dispose();
		};
	}, [open, pdfUrl]);

	const handleSave = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not ready');
			return;
		}

		setIsSaving(true);
		setError(null);

		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();
			if (!doc) {
				showToast('error', 'No document loaded');
				return;
			}

			const xfdfString = await annotationManager.exportAnnotations();
			const data = await doc.getFileData({ xfdfString });
			const arr = new Uint8Array(data);
			const safeName = documentTitle.trim().replace(/\.pdf$/i, '') || 'document';
			const file = new File([arr], `${safeName}.pdf`, { type: 'application/pdf' });

			await onSave(file);
			showToast('success', 'PDF saved');
			onClose();
		} catch (e) {
			console.error('Save PDF error:', e);
			setError('Could not export PDF. Please try again.');
			showToast('error', 'Failed to save PDF');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={isSaving ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {
					minHeight: '90vh',
					maxWidth: '1200px',
				},
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<DescriptionIcon />
					<Typography variant="h6">Edit PDF: {documentTitle}</Typography>
				</Box>
			</DialogTitle>

			<DialogContent>
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2">
						Fill forms, add annotations, or sign as needed. Click &quot;Save PDF&quot; to apply
						changes only.
					</Typography>
				</Alert>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Box
					sx={{
						height: '70vh',
						width: '100%',
						border: '1px solid #ddd',
						borderRadius: 1,
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					{(!isViewerReady || !pdfUrl) && open && (
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								bgcolor: 'rgba(255, 255, 255, 0.9)',
								zIndex: 10,
							}}
						>
							<Box textAlign="center">
								<CircularProgress />
								<Typography variant="body2" sx={{ mt: 2 }}>
									Loading document viewer...
								</Typography>
							</Box>
						</Box>
					)}
					<div ref={viewer} style={{ height: '100%', width: '100%' }} />
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					className={styles.mabelButton}
					onClick={onClose}
					disabled={isSaving}
					sx={{
						backgroundColor: 'rgb(68, 99, 136)',
						'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
					}}
				>
					Cancel
				</Button>
				<Box sx={{ flex: '1 1 auto' }} />
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={isSaving || !isViewerReady || !pdfUrl}
					startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
					sx={{
						backgroundColor: 'rgb(68, 99, 136)',
						'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
					}}
				>
					{isSaving ? 'Saving...' : 'Save PDF'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
